/**
 * Consultas e gravações dos documentos pelo TanStack Query.
 *
 * Papel: aqui ficam `useDocumentos`, que mantém em cache o que a tela lê, e
 * `useSalvarDocumento` e `useExcluirDocumento`, que gravam e mandam a listagem
 * se atualizar.
 *
 * Como usar: toda chave deste módulo começa por `CHAVE_DOCUMENTOS`, e as duas
 * mutações invalidam o prefixo inteiro. O alerta de vencimento da tela inicial
 * lê a mesma consulta, então salvar um documento com validade nova acende ou
 * apaga o alerta sem que ninguém precise recarregar a página.
 */
import { type NovoDocumento } from '@concierge/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  atualizarDocumento,
  buscarDocumentos,
  criarDocumento,
  excluirDocumento,
} from '../api/documentos';

/** Prefixo de todas as chaves de cache deste módulo. */
export const CHAVE_DOCUMENTOS = ['documentos'] as const;

/** Chave da lista de documentos. */
export const CHAVE_LISTA_DOCUMENTOS = [...CHAVE_DOCUMENTOS, 'lista'] as const;

/**
 * Lê os documentos cadastrados.
 *
 * O interruptor existe para a tela inicial, que monta o aviso de vencimento sem
 * saber de antemão quais módulos a pessoa tem: hook não se chama
 * condicionalmente, e a consulta feita por quem não tem `documentos` voltaria
 * 403 a cada abertura. Com `habilitado` falso, o hook é chamado e a requisição
 * não sai.
 *
 * @param habilitado - Se a consulta deve mesmo ir à API. Padrão verdadeiro.
 * @returns Resultado da consulta, com a pessoa aninhada em cada documento.
 */
export function useDocumentos(habilitado = true) {
  return useQuery({
    queryKey: CHAVE_LISTA_DOCUMENTOS,
    queryFn: ({ signal }) => buscarDocumentos(signal),
    enabled: habilitado,
  });
}

/** Variáveis de `useSalvarDocumento`. */
interface SalvarDocumentoVariaveis {
  /** Identificador do documento a alterar, ou `null` para criar um novo. */
  id: string | null;
  dados: NovoDocumento;
}

/**
 * Cria ou altera um documento, conforme venha identificador.
 *
 * O `mutateAsync` rejeita com `ErroDaApi` quando a API recusa, e o formulário
 * precisa tratar essa rejeição — 409 significa que aquela pessoa já tem um
 * documento daquele tipo.
 *
 * @returns A mutação; `isPending` verdadeiro enquanto a gravação não responde.
 */
export function useSalvarDocumento() {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dados }: SalvarDocumentoVariaveis) =>
      id === null ? criarDocumento(dados) : atualizarDocumento(id, dados),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: CHAVE_DOCUMENTOS }),
  });
}

/**
 * Apaga um documento.
 *
 * @returns A mutação; `isPending` verdadeiro enquanto a exclusão não responde.
 */
export function useExcluirDocumento() {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => excluirDocumento(id),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: CHAVE_DOCUMENTOS }),
  });
}
